import { Send, Notes, CalendarMonth, ScheduleSend, Person, Email, CheckCircle, Delete, Book, Celebration, AutoAwesome } from '@mui/icons-material';

const API_BASE_URL = 'https://prplusapi.nekoai.io/';

const DEVELOPER_MODE = import.meta.env.VITE_DEV_ENV === 'true';

const DEMO_USER_ID = 2;

const USER_TYPES = {
  STAFF: 0,
  EXPERT: 1,
  DEMO: 2,
};

const PATHS = {
  HOME: { value: '/', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  AUTH: {
    LOGIN: { value: '/auth/login' },
    EXPERT_AUTH: { value: '/auth/company/:token' },
  },
  PROFILE: {
    INDEX: { value: '/profile', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
    EDIT: { value: '/profile/edit', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  },
  EXPERT: { value: '/experts', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  DISCARDED: { value: '/discarded', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  QUERIES: { value: '/queries', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  QUERY_PROFILE: { value: '/query-profile/:queryID', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.EXPERT, USER_TYPES.DEMO] },

  PITCH_INBOX: { value: '/pitchInbox', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  SUBMITTED: { value: '/submitted', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  SUCCESSFUL: { value: '/successful', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  REPURPOSE: { value: '/repurpose', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  REPORTS: {
    INDEX: { value: '/reports', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
    PROFILE: { value: '/reports/:companyID', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.EXPERT, USER_TYPES.DEMO] },
  },
  WINS_DASHBOARD: {
    INDEX: { value: '/wins-dashboard', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
    PROFILE: { value: '/wins-dashboard/:companyID', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.EXPERT, USER_TYPES.DEMO] },
  },
  MANAGE_OUTLETS: { value: '/manage-outlets', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  MANAGE_KEYWORDS: { value: '/manage-keywords', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  TOOLS: { value: '/tools', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.DEMO] },
  DOCS: { value: '/docs', accessibleBy: [USER_TYPES.STAFF] },
  NOT_FOUND: { value: '/404', accessibleBy: [USER_TYPES.STAFF, USER_TYPES.EXPERT, USER_TYPES.DEMO] },
};

const NAV_BAR_ROUTES = {
  TOP_ROUTES: [
    {
      path: PATHS.PITCH_INBOX.value,
      label: 'Pitch Inbox',
      Icon: Send,
      status: [
        'new',
        'draft',
        'pending',
        'awaiting',
        'clientApproved',
        'clientNeedsEdits',
        'needsFollowUp',
        'inquireInterest',
        'interviewOpportunity',
        'pendingPublication',
        'paidEditorial',
      ],
    },
    {
      path: PATHS.SUCCESSFUL.value,
      label: 'Successful',
      Icon: Celebration,
      status: ['successful'],
    },
    {
      path: PATHS.REPURPOSE.value,
      label: 'Repurpose',
      Icon: ScheduleSend,
      status: ['repurposed', 'insightsNotIncluded', 'clientPassed', 'journalistPassed'],
    },
    { path: PATHS.DISCARDED.value, label: 'Discarded', Icon: Delete, status: 'discarded' },
  ],
  BOTTOM_ROUTES: [
    { path: PATHS.TOOLS.value, label: 'Tools & Reports', Icon: AutoAwesome, status: 'tools' },
    { path: PATHS.DOCS.value, label: 'Docs', Icon: Book, status: 'docs' },
  ],
  HIDDEN_ROUTES: [],
};

const QUERY_STATUSES = {
  new: { label: 'New', color: 'success', path: PATHS.PITCH_INBOX.value, hideInOptions: true },
  draft: { label: 'Draft', color: 'info', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  discarded: { label: 'Discarded', color: 'error', path: PATHS.DISCARDED.value },
  clientPassed: { label: 'Client passed/missed', color: 'error', path: PATHS.REPURPOSE.value },
  awaiting: { label: 'Sent to client', color: 'info', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  inquireInterest: { label: 'Sent for client insight/interest', color: 'info', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  interviewOpportunity: { label: 'Interview opportunity', color: 'info', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  clientNeedsEdits: { label: 'Needs edits', color: 'purple', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  needsFollowUp: { label: 'Needs follow up', color: 'purple', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  clientApproved: { label: 'Client approved', color: 'success', path: `${PATHS.PITCH_INBOX.value}?status=pitchInbox` },
  pending: { label: 'Submitted to journalist', color: 'purple', path: `${PATHS.PITCH_INBOX.value}?status=pending` },
  successful: { label: 'Successful placement', color: 'success', path: PATHS.SUCCESSFUL.value },
  repurposed: { label: 'Repurpose', color: 'brown', path: PATHS.REPURPOSE.value },
  pendingPublication: { label: 'Pending publication', color: 'neutral', path: `${PATHS.PITCH_INBOX.value}?status=pending` },
  paidEditorial: { label: 'Paid editorial', color: 'success', path: `${PATHS.PITCH_INBOX.value}?status=pending` },
  journalistPassed: { label: 'Journalist passed', color: 'error', path: PATHS.REPURPOSE.value },
  insightsNotIncluded: { label: 'Insights not included', color: 'warning', path: PATHS.REPURPOSE.value },
};

const TAB_CONFIG = {
  SUCCESSFUL: {
    id: 0,
    icon: CheckCircle,
    label: 'Successful placement',
    requiresExpert: true,
  },
  NOTES: {
    id: 1,
    icon: Notes,
    label: 'Notes',
    requiresExpert: false,
  },
  EXPERT: {
    id: 2,
    icon: Person,
    label: 'Expert',
    requiresExpert: true,
  },
  ACTIVITY: {
    id: 3,
    icon: CalendarMonth,
    label: 'Activity',
    requiresExpert: true,
  },
  EMAIL: {
    id: 4,
    icon: Email,
    label: 'Email expert',
    requiresExpert: true,
  },
  SUBMIT: {
    id: 5,
    icon: Send,
    label: 'Submit',
    requiresExpert: true,
  },
};

const QUERY_TABLE_VISIBILITY = {
  DEFAULT: {
    id: DEVELOPER_MODE,
    sentToExpertDate: false,
  },
  STATUS_RULES: {
    new: {
      status: false,
      claimedBy: false,
      sentToExpertDate: false,
      company: false,
      expert: false,
      dateWriting: false,
    },
    repurposed: {
      status: false,
      claimedBy: false,
      actions: false,
      dateWriting: false,
    },
    draft: {
      sentToExpertDate: false,
      status: true,
      actions: false,
    },
    pending: {
      actions: false,
      dateWriting: false,
    },
    submittedToJournalist: {
      actions: false,
      dateWriting: false,
    },
    pendingPublication: {
      actions: false,
      dateWriting: false,
    },
    successful: {
      dateWriting: false,
      actions: false,
    },
    discarded: {
      actions: false,
    },
    clientPassed: {
      actions: false,
    },
    awaiting: {
      actions: false,
    },
    inquireInterest: {
      actions: false,
    },
    interviewOpportunity: {
      actions: false,
    },
    clientNeedsEdits: {
      actions: false,
    },
    needsFollowUp: {
      actions: false,
    },
    clientApproved: {
      actions: false,
    },
    paidEditorial: {
      actions: false,
    },
    journalistPassed: {
      actions: false,
    },
    insightsNotIncluded: {
      actions: false,
    },
  },
};

const QUERY_SOURCES = {
  qwoted: 'Qwoted',
  haro: 'HARO',
  helpab2b: 'HelpAB2B',
  existing_contact: 'Existing Contact',
  interview: 'Interview',
  guest_blog: 'Guest Blog',
  other: 'Other',
};

export {
  API_BASE_URL,
  DEVELOPER_MODE,
  DEMO_USER_ID,
  USER_TYPES,
  PATHS,
  NAV_BAR_ROUTES,
  QUERY_STATUSES,
  TAB_CONFIG,
  QUERY_TABLE_VISIBILITY,
  QUERY_SOURCES,
};
